<template>
    <v-container :fluid="true">
        <v-row>
            <v-col class="mx-auto" cols="12" md="8">
                <v-card elevation="3">
                    <v-form v-model="formData.valid" class="mb-3" validate-on="blur" @submit.prevent="updateSettings">
                        <v-card-title id="heading" class="text-start font-weight-bold">Preferences</v-card-title>

                        <v-container>
                            <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                                     density="compact" variant="tonal">
                                <p v-for="error in errors">{{ error }}</p>
                            </v-alert>
                            <v-row>
                                <!--<v-col cols="12" sm="6">
                                        <v-switch v-model="formData['parameters']['sidebar']" color="primary"
                                            density="compact" label="Keep Sidebar Visible"/>
                                </v-col>-->
                                <v-col v-if="landingOptions.length > 1" cols="12" sm="6">
                                    <v-select v-model="formData['parameters']['landing_page']" :items="landingOptions"
                                              density="compact" label="Open on login" variant="outlined"/>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-radio-group v-model="formData['parameters']['distance_unit']" color="primary"
                                                   inline
                                                   label="Distance units">
                                        <v-radio label="Miles" value="miles"/>
                                        <v-radio label="Kilometers" value="km"/>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-container>

                        <v-card-actions class="px-4 d-flex justify-end">
                            <v-btn color="primary" size="small" type="submit">Save</v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>

                <!-- Product Tour. Guided tours target the company-user onboarding UI
                     and only auto-run for company users (see TourManager), so only
                     offer the replay controls to company users. -->
                <v-card v-if="authStore.user?.type === 'company'" elevation="3" class="mt-4">
                    <v-card-title class="text-start font-weight-bold">Product Tour</v-card-title>
                    <v-card-text>
                        <p class="text-body-2 text-medium-emphasis mb-3">
                            Re-watch guided tours to familiarize yourself with the platform.
                        </p>
                        <div class="d-flex flex-wrap ga-3">
                            <v-btn variant="outlined" size="small" prepend-icon="mdi-play-circle-outline"
                                   @click="replayTour('welcome')">
                                Welcome Tour
                            </v-btn>
                            <v-btn variant="outlined" size="small" prepend-icon="mdi-play-circle-outline"
                                   @click="replayTour('live-view')">
                                Live View Tour
                            </v-btn>
                            <v-btn variant="outlined" size="small" prepend-icon="mdi-play-circle-outline"
                                   @click="replayTour('monitoring')">
                                Alerts Tour
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {reactive, ref} from "vue";
import {useRouter} from 'vue-router';
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {resetTour} from "@/composables/useProductTour";
import {landingPagesFor, landingRoute} from "@/composables/globals";

const router = useRouter();
const errors = ref([]);
const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    user: true,
    valid: false,
    parameters: {
        sidebar: !!parseInt(authStore.configs.sidebar),
        distance_unit: authStore.configs.distance_unit ?? 'miles',
        landing_page: null,
    },
})
// The saved screen when the role can open it, else the first screen it can
const landingOptions = landingPagesFor(authStore.menus);
formData.parameters.landing_page = landingOptions.some(o => o.value === landingRoute(authStore))
    ? landingRoute(authStore) : (landingOptions[0]?.value ?? 'dashboard');
const savedLanding = formData.parameters.landing_page;
const tourRoutes = {welcome: 'dashboard', 'live-view': 'live-view', monitoring: 'monitoring-configs'};
const replayTour = (tourName) => {
    resetTour(tourName);
    showSnackbar('Tour reset! Navigating...', 'info');
    router.push({name: tourRoutes[tourName]});
};

const updateSettings = () => {
    if (formData.valid) {
        errors.value = [];
        // Only sent when changed, so saving other settings never depends on it
        const payload = {...formData, parameters: {...formData.parameters}};
        if (payload.parameters.landing_page === savedLanding) delete payload.parameters.landing_page;
        send('post', `settings`, payload)
            .then((response) => {
                showSnackbar(response.data.message);
                authStore.updateAuthConfig(response.data.data);
                router.go(); // Reloads the current route
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            });
    }
}
</script>
